import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const tabs = [
  { key: "profile", label: "School Profile", icon: "bi-building" },
  { key: "currencies", label: "Currencies", icon: "bi-currency-exchange" },
  { key: "payments", label: "Payment Methods", icon: "bi-credit-card" },
  { key: "finance", label: "Finance Settings", icon: "bi-receipt" },
];

const emptyCurrency = {
  id: null,
  code: "",
  name: "",
  symbol: "",
  is_default: false,
  is_active: true,
  exchange_rate_to_default: "1",
  decimal_places: 2,
  notes: "",
};

const emptyPaymentMethod = {
  id: null,
  name: "",
  channel: "CASH",
  currency: "",
  account_name: "",
  account_number: "",
  instructions: "",
  is_active: true,
};

function SchoolFinanceSettings() {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [schoolProfile, setSchoolProfile] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [financeSettings, setFinanceSettings] = useState({});
  const [currencyForm, setCurrencyForm] = useState(emptyCurrency);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentMethod);
  const [logoFile, setLogoFile] = useState(null);

  const activeCurrencies = useMemo(
    () => currencies.filter((currency) => currency.is_active),
    [currencies]
  );

  const loadSettings = async () => {
    try {
      setLoading(true);
      await axiosInstance.post("settings/school-finance/bootstrap/");

      const [profileResponse, currenciesResponse, methodsResponse, financeResponse] =
        await Promise.all([
          axiosInstance.get("settings/school-profile/"),
          axiosInstance.get("settings/currencies/"),
          axiosInstance.get("settings/payment-methods/"),
          axiosInstance.get("settings/finance/"),
        ]);

      setSchoolProfile(profileResponse.data || {});
      setCurrencies(currenciesResponse.data || []);
      setPaymentMethods(methodsResponse.data || []);
      setFinanceSettings(financeResponse.data || {});
    } catch (error) {
      console.error(error);
      toast.error("Failed to load school and finance settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const updateSchoolProfile = (field, value) => {
    setSchoolProfile((prev) => ({ ...prev, [field]: value }));
  };

  const updateFinanceSettings = (field, value) => {
    setFinanceSettings((prev) => ({ ...prev, [field]: value }));
  };

  const saveSchoolProfile = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      let payload = schoolProfile;
      let config = {};

      if (logoFile) {
        payload = new FormData();
        Object.entries(schoolProfile).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            payload.append(key, value);
          }
        });
        payload.append("logo", logoFile);
        config = { headers: { "Content-Type": "multipart/form-data" } };
      }

      const response = await axiosInstance.put("settings/school-profile/", payload, config);
      setSchoolProfile(response.data || {});
      setLogoFile(null);
      toast.success("School profile saved.");
    } catch (error) {
      toast.error("Failed to save school profile.");
    } finally {
      setLoading(false);
    }
  };

  const saveFinanceSettings = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const response = await axiosInstance.put("settings/finance/", financeSettings);
      setFinanceSettings(response.data || {});
      toast.success("Finance settings saved.");
    } catch (error) {
      toast.error("Failed to save finance settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCurrencyForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePaymentChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPaymentForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveCurrency = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (currencyForm.id) {
        await axiosInstance.patch(`settings/currencies/${currencyForm.id}/`, currencyForm);
        toast.success("Currency updated.");
      } else {
        await axiosInstance.post("settings/currencies/", currencyForm);
        toast.success("Currency added.");
      }
      setCurrencyForm(emptyCurrency);
      await loadSettings();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to save currency.");
    } finally {
      setLoading(false);
    }
  };

  const savePaymentMethod = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (paymentForm.id) {
        await axiosInstance.patch(`settings/payment-methods/${paymentForm.id}/`, paymentForm);
        toast.success("Payment method updated.");
      } else {
        await axiosInstance.post("settings/payment-methods/", paymentForm);
        toast.success("Payment method added.");
      }
      setPaymentForm(emptyPaymentMethod);
      await loadSettings();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to save payment method.");
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (url, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(url);
      toast.success(`${label} deleted.`);
      await loadSettings();
    } catch (error) {
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">School & Finance Setup</h5>
              <p className="text-muted mb-0">
                Set school identity, currencies, payment channels, and finance numbering before Fees & Billing.
              </p>
            </div>
            <button className="btn btn-outline-success" onClick={loadSettings} disabled={loading}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>

          <div className="row g-2 mt-3">
            <Summary label="Currencies" value={currencies.length} />
            <Summary label="Payment Methods" value={paymentMethods.length} />
            <Summary label="Default Currency" value={currencies.find((item) => item.is_default)?.code || "Not set"} />
            <Summary label="Multi-currency" value={financeSettings.allow_multi_currency_payments ? "Enabled" : "Disabled"} />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-sm ${activeTab === tab.key ? "btn-success" : "btn-light border"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`bi ${tab.icon} me-2`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "profile" && (
        <SetupCard title="School Profile">
          <form onSubmit={saveSchoolProfile}>
            <div className="row g-3">
              <TextField label="School Name" value={schoolProfile.name || ""} onChange={(value) => updateSchoolProfile("name", value)} required />
              <TextField label="Short Name" value={schoolProfile.short_name || ""} onChange={(value) => updateSchoolProfile("short_name", value)} />
              <TextField label="Motto" value={schoolProfile.motto || ""} onChange={(value) => updateSchoolProfile("motto", value)} />
              <div className="col-md-4">
                <label className="form-label fw-semibold">School Logo</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => setLogoFile(event.target.files?.[0] || null)}
                />
                <small className="text-muted d-block">
                  PNG logo is recommended for receipts.
                </small>
                {schoolProfile.logo && (
                  <small className="text-success d-block mt-1">
                    Current logo is already saved.
                  </small>
                )}
                {logoFile && (
                  <small className="text-success d-block mt-1">
                    Selected: {logoFile.name}
                  </small>
                )}
              </div>
              <TextField label="Phone" value={schoolProfile.phone || ""} onChange={(value) => updateSchoolProfile("phone", value)} />
              <TextField label="Alternative Phone" value={schoolProfile.alternative_phone || ""} onChange={(value) => updateSchoolProfile("alternative_phone", value)} />
              <TextField label="Email" type="email" value={schoolProfile.email || ""} onChange={(value) => updateSchoolProfile("email", value)} />
              <TextField label="Website" value={schoolProfile.website || ""} onChange={(value) => updateSchoolProfile("website", value)} />
              <TextField label="City" value={schoolProfile.city || ""} onChange={(value) => updateSchoolProfile("city", value)} />
              <TextField label="Province" value={schoolProfile.province || ""} onChange={(value) => updateSchoolProfile("province", value)} />
              <div className="col-12">
                <label className="form-label fw-semibold">Address</label>
                <textarea className="form-control" rows="2" value={schoolProfile.address || ""} onChange={(event) => updateSchoolProfile("address", event.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Report Header</label>
                <textarea className="form-control" rows="3" value={schoolProfile.report_header || ""} onChange={(event) => updateSchoolProfile("report_header", event.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Receipt Footer</label>
                <textarea className="form-control" rows="3" value={schoolProfile.receipt_footer || ""} onChange={(event) => updateSchoolProfile("receipt_footer", event.target.value)} />
              </div>
            </div>
            <SaveBar loading={loading} label="Save School Profile" />
          </form>
        </SetupCard>
      )}

      {activeTab === "currencies" && (
        <SetupCard title="Currencies">
          <form className="row g-3 mb-4" onSubmit={saveCurrency}>
            <TextField label="Code" value={currencyForm.code} onChange={(value) => setCurrencyForm((prev) => ({ ...prev, code: value.toUpperCase() }))} placeholder="USD / ZWG / ZAR" required />
            <TextField label="Name" value={currencyForm.name} onChange={(value) => setCurrencyForm((prev) => ({ ...prev, name: value }))} required />
            <TextField label="Symbol" value={currencyForm.symbol} onChange={(value) => setCurrencyForm((prev) => ({ ...prev, symbol: value }))} />
            <NumberField label="Rate To Default" name="exchange_rate_to_default" value={currencyForm.exchange_rate_to_default} onChange={handleCurrencyChange} step="0.000001" />
            <NumberField label="Decimals" name="decimal_places" value={currencyForm.decimal_places} onChange={handleCurrencyChange} />
            <CheckField label="Default" name="is_default" checked={currencyForm.is_default} onChange={handleCurrencyChange} />
            <CheckField label="Active" name="is_active" checked={currencyForm.is_active} onChange={handleCurrencyChange} />
            <div className="col-12">
              <input name="notes" className="form-control" placeholder="Notes, e.g. manual rate updated weekly" value={currencyForm.notes || ""} onChange={handleCurrencyChange} />
            </div>
            <SaveBar loading={loading} label={currencyForm.id ? "Update Currency" : "Add Currency"} onCancel={currencyForm.id ? () => setCurrencyForm(emptyCurrency) : null} />
          </form>

          <SimpleTable
            headers={["Code", "Name", "Symbol", "Rate", "Default", "Status", "Actions"]}
            rows={currencies.map((currency) => [
              currency.code,
              currency.name,
              currency.symbol || "-",
              currency.exchange_rate_to_default,
              currency.is_default ? "Yes" : "No",
              <Status value={currency.is_active} />,
              <ActionButtons onEdit={() => setCurrencyForm(currency)} onDelete={() => deleteRecord(`settings/currencies/${currency.id}/`, currency.code)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "payments" && (
        <SetupCard title="Payment Methods">
          <form className="row g-3 mb-4" onSubmit={savePaymentMethod}>
            <TextField label="Method Name" value={paymentForm.name} onChange={(value) => setPaymentForm((prev) => ({ ...prev, name: value }))} placeholder="USD EcoCash" required />
            <SelectField label="Channel" name="channel" value={paymentForm.channel} onChange={handlePaymentChange} options={[
              ["CASH", "Cash"],
              ["MOBILE_MONEY", "Mobile Money"],
              ["BANK", "Bank"],
              ["CARD", "Card"],
              ["OTHER", "Other"],
            ]} />
            <SelectField label="Currency" name="currency" value={paymentForm.currency} onChange={handlePaymentChange} options={activeCurrencies.map((item) => [item.id, `${item.code} - ${item.name}`])} />
            <TextField label="Account Name" value={paymentForm.account_name || ""} onChange={(value) => setPaymentForm((prev) => ({ ...prev, account_name: value }))} />
            <TextField label="Account Number" value={paymentForm.account_number || ""} onChange={(value) => setPaymentForm((prev) => ({ ...prev, account_number: value }))} />
            <CheckField label="Active" name="is_active" checked={paymentForm.is_active} onChange={handlePaymentChange} />
            <div className="col-12">
              <input name="instructions" className="form-control" placeholder="Payment instructions shown on receipts/invoices" value={paymentForm.instructions || ""} onChange={handlePaymentChange} />
            </div>
            <SaveBar loading={loading} label={paymentForm.id ? "Update Method" : "Add Method"} onCancel={paymentForm.id ? () => setPaymentForm(emptyPaymentMethod) : null} />
          </form>

          <SimpleTable
            headers={["Method", "Channel", "Currency", "Account", "Status", "Actions"]}
            rows={paymentMethods.map((method) => [
              method.name,
              method.channel,
              method.currency_code,
              method.account_number || "-",
              <Status value={method.is_active} />,
              <ActionButtons onEdit={() => setPaymentForm(method)} onDelete={() => deleteRecord(`settings/payment-methods/${method.id}/`, method.name)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "finance" && (
        <SetupCard title="Finance Settings">
          <form onSubmit={saveFinanceSettings}>
            <div className="row g-3">
              <TextField label="Invoice Prefix" value={financeSettings.invoice_prefix || ""} onChange={(value) => updateFinanceSettings("invoice_prefix", value)} required />
              <TextField label="Receipt Prefix" value={financeSettings.receipt_prefix || ""} onChange={(value) => updateFinanceSettings("receipt_prefix", value)} required />
              <NumberInput label="Next Invoice Number" value={financeSettings.next_invoice_number || 1} onChange={(value) => updateFinanceSettings("next_invoice_number", value)} />
              <NumberInput label="Next Receipt Number" value={financeSettings.next_receipt_number || 1} onChange={(value) => updateFinanceSettings("next_receipt_number", value)} />
              <NumberInput label="Default Due Days" value={financeSettings.default_due_days || 30} onChange={(value) => updateFinanceSettings("default_due_days", value)} />
              <Switch label="Allow partial payments" checked={Boolean(financeSettings.allow_partial_payments)} onChange={(value) => updateFinanceSettings("allow_partial_payments", value)} />
              <Switch label="Allow multi-currency payments" checked={Boolean(financeSettings.allow_multi_currency_payments)} onChange={(value) => updateFinanceSettings("allow_multi_currency_payments", value)} />
              <Switch label="Show balance in default currency" checked={Boolean(financeSettings.show_balance_in_default_currency)} onChange={(value) => updateFinanceSettings("show_balance_in_default_currency", value)} />
              <Switch label="Auto-allocate payments" checked={Boolean(financeSettings.auto_allocate_payments)} onChange={(value) => updateFinanceSettings("auto_allocate_payments", value)} />
            </div>
            <SaveBar loading={loading} label="Save Finance Settings" />
          </form>
        </SetupCard>
      )}
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="border rounded-3 p-3 bg-light h-100">
        <small className="text-muted fw-bold">{label}</small>
        <div className="fs-5 fw-bold" style={{ color: "#125b2d" }}>{value}</div>
      </div>
    </div>
  );
}

function SetupCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white"><h6 className="fw-bold mb-0">{title}</h6></div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder = "", type = "text", required = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type={type} className="form-control" placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} required={required} />
    </div>
  );
}

function NumberField({ label, name, value, onChange, step = "1" }) {
  return (
    <div className="col-md-2">
      <label className="form-label fw-semibold">{label}</label>
      <input type="number" step={step} min="0" name={name} className="form-control" value={value} onChange={onChange} />
    </div>
  );
}

function NumberInput({ label, value, onChange }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type="number" min="1" className="form-control" value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select name={name} className="form-select" value={value || ""} onChange={onChange} required>
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </div>
  );
}

function CheckField({ label, name, checked, onChange }) {
  return (
    <div className="col-md-2 d-flex align-items-end">
      <label className="form-check form-switch mb-2">
        <input type="checkbox" name={name} className="form-check-input" checked={checked} onChange={onChange} />
        <span className="form-check-label fw-semibold">{label}</span>
      </label>
    </div>
  );
}

function Switch({ label, checked, onChange }) {
  return (
    <div className="col-md-6">
      <label className="form-check form-switch border rounded-3 p-3 h-100">
        <input type="checkbox" className="form-check-input ms-0 me-2" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        <span className="form-check-label fw-semibold">{label}</span>
      </label>
    </div>
  );
}

function SaveBar({ loading, label, onCancel }) {
  return (
    <div className="col-12 d-flex flex-wrap gap-2 mt-3">
      <button className="btn btn-success fw-semibold" disabled={loading}>{loading ? "Saving..." : label}</button>
      {onCancel && <button type="button" className="btn btn-light border fw-semibold" onClick={onCancel}>Cancel Edit</button>}
    </div>
  );
}

function Status({ value }) {
  return <span className={`badge ${value ? "bg-success" : "bg-secondary"}`}>{value ? "Active" : "Inactive"}</span>;
}

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div className="d-flex align-items-center gap-1">
      <button type="button" className="btn btn-sm btn-light border" title="Edit" onClick={onEdit}><i className="bi bi-pencil-square text-success"></i></button>
      <button type="button" className="btn btn-sm btn-light border" title="Delete" onClick={onDelete}><i className="bi bi-trash text-danger"></i></button>
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light"><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>
          {rows.length ? rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>) : (
            <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default SchoolFinanceSettings;
